import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faMobile, faLaptop, faSync } from '@fortawesome/free-solid-svg-icons';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import SuccessGuaranteeBadge from './SuccessGuaranteeBadge';

const StudyAnywhere = ({ studyAnywhere, downloadLinks, theme }) => {
  const styles = {
    container: {
      marginTop: '15px',
      textAlign: 'center',
      scrollMarginTop: '80px',
      paddingTop: '20px', // Add some top padding for better spacing
    },
    content: {
      maxWidth: '1200px',
      margin: '0 auto'
    },
    title: {
      fontSize: 'clamp(1.75rem, 5vw, 2.5rem)',
      fontWeight: '800',
      color: theme?.text?.primary,
      marginBottom: '0.75rem'
    },
    subtitle: {
      fontSize: 'clamp(0.95rem, 2.5vw, 1.125rem)',
      color: theme?.text?.secondary,
      marginBottom: 'clamp(1.5rem, 4vw, 3rem)',
      maxWidth: '800px',
      marginLeft: 'auto',
      marginRight: 'auto',
      lineHeight: '1.6',
      padding: '0 0.5rem'
    },
    features: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
      gap: 'clamp(1rem, 3vw, 2rem)',
      marginBottom: 'clamp(1.5rem, 4vw, 3rem)'
    },
    feature: {
      padding: '1.5rem',
      background: theme?.background?.card,
      borderRadius: '1rem',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
      border: `1px solid ${theme?.borders?.primary}`
    },
    featureHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '1rem',
      gap: '1rem'
    },
    featureIcon: {
      fontSize: '2rem',
      color: theme?.primary?.blue?.[500],
      flexShrink: 0
    },
    featureTitle: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      margin: 0
    },
    featureDescription: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
      lineHeight: '1.5'
    },
    downloadSection: {
      marginTop: '1.5rem',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '1rem'
    },
    downloadTitle: {
      fontSize: 'clamp(1.125rem, 3vw, 1.5rem)',
      fontWeight: '600',
      color: theme?.text?.primary,
      margin: 0
    },
    downloadButtons: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '300px',
      gap: '0.75rem'
    },
    downloadButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.75rem 1.25rem',
      minHeight: '48px',
      background: theme?.neutral?.white,
      border: `2px solid ${theme?.primary?.blue?.[500]}`,
      borderRadius: '0.625rem',
      color: theme?.primary?.blue?.[500],
      textDecoration: 'none',
      fontWeight: '600',
      fontSize: 'clamp(0.875rem, 2vw, 1rem)',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    downloadButtonHover: {
      background: theme?.primary?.blue?.[500],
      color: theme?.neutral?.white,
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
    }
  };

  return (
    <section id="download" style={styles.container}>
      
      <div style={styles.content}>
        <h2 style={styles.title}>{studyAnywhere.title}</h2>
        <p style={styles.subtitle}>{studyAnywhere.subtitle}</p>
        
                <div style={styles.features}>
          <div style={styles.feature}>
            <div style={styles.featureHeader}>
              <FontAwesomeIcon icon={faMobile} style={styles.featureIcon} />
              <h3 style={styles.featureTitle}>Mobile Apps</h3>
            </div>
            <p style={styles.featureDescription}>
              Native iOS and Android apps with offline access and smart notifications.
            </p>
          </div>
          
          <div style={styles.feature}>
            <div style={styles.featureHeader}>
              <FontAwesomeIcon icon={faLaptop} style={styles.featureIcon} />
              <h3 style={styles.featureTitle}>Web Platform</h3>
            </div>
            <p style={styles.featureDescription}>
              Full browser experience. No downloads needed, study anywhere with internet.
            </p>
          </div>
          
          <div style={styles.feature}>
            <div style={styles.featureHeader}>
              <FontAwesomeIcon icon={faSync} style={styles.featureIcon} />
              <h3 style={styles.featureTitle}>Seamless Sync</h3>
            </div>
            <p style={styles.featureDescription}>
              Automatic sync across all devices. Continue exactly where you left off.
            </p>
          </div>
        </div>

        <div style={styles.downloadSection} className="download-section">
          <h3 style={styles.downloadTitle}>Download Now:</h3>
          <div style={styles.downloadButtons} className="download-buttons">
            {downloadLinks?.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                style={styles.downloadButton}
                onMouseEnter={(e) => {
                  Object.assign(e.target.style, styles.downloadButtonHover);
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = styles.downloadButton.background;
                  e.target.style.color = styles.downloadButton.color;
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                {link.store === 'Apple' ? (
                  <FontAwesomeIcon icon={faApple} />
                ) : link.store === 'Google' ? (
                  <FontAwesomeIcon icon={faGooglePlay} />
                ) : (
                  <FontAwesomeIcon icon={faDownload} />
                )}
                {link.title}
              </a>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (min-width: 640px) {
          .download-section {
            flex-direction: row !important;
            gap: 2rem !important;
          }
          .download-buttons {
            flex-direction: row !important;
            width: auto !important;
            max-width: none !important;
          }
        }
      `}</style>
    </section>
  );
};

export default StudyAnywhere;
